<template>
	<el-dialog
		title="管理分组"
		:visible.sync="visibleProxy"
		width="600px"
		append-to-body
		custom-class="gm-dialog"
		@closed="onClosed"
	>
		<div class="gm-subtitle">仅展示自定义的分组</div>
		<div class="gm-table">
			<div class="gm-thead">
				<div class="gm-th">自定义组名</div>
				<div class="gm-th">快捷短语数量</div>
				<div class="gm-th">操作</div>
			</div>
			<div class="gm-tbody">
				<div class="gm-tr" v-for="group in customGroups" :key="group.id">
					<div class="gm-td gm-name">
						<el-input 
							v-if="editingGroupId === group.id" 
							v-model="editingGroupName" 
							size="small" 
							@keydown.enter.native="onSaveGroup(group)"
							@keydown.esc.native="onCancelEdit"
							@blur="onSaveGroup(group)"
							ref="editInput"
						></el-input>
						<span 
							v-else 
							class="gm-name-text" 
							@click="onEditGroup(group)"
							:class="{ 'gm-name-hover': hoveredGroupId === group.id }"
							@mouseenter="hoveredGroupId = group.id"
							@mouseleave="hoveredGroupId = null"
						>{{ group.name }}</span>
					</div>
					<div class="gm-td gm-count">{{ group.replyList.length || 0 }}</div>
					<div class="gm-td gm-actions">
						<el-button type="text" class="gm-delete-btn" @click="onDeleteGroup(group)">删除</el-button>
					</div>
				</div>
				<div class="gm-empty" v-if="!customGroups || customGroups.length === 0">暂无自定义分组</div>
				<!-- 新增分组行 -->
				<div class="gm-tr gm-add-row" v-if="isAddingNew">
					<div class="gm-td gm-name">
						<el-input 
							v-model="newGroupName" 
							size="small" 
							placeholder="请输入分组名称"
							@keydown.enter.native="onSaveNewGroup"
							@keydown.esc.native="onCancelAdd"
							@blur="onSaveNewGroup"
							ref="newGroupInput"
						></el-input>
					</div>
					<div class="gm-td gm-count">0</div>
					<div class="gm-td gm-actions">
						<el-button type="text" class="gm-cancel-btn" @click="onCancelAdd">取消</el-button>
					</div>
				</div>
			</div>
		</div>
		<div class="gm-add-group">
			<el-button class="gm-add-btn" @click="onStartAddGroup">+ 新建分组</el-button>
		</div>
		<span slot="footer" class="dialog-footer">
			<el-button @click="visibleProxy=false">取消</el-button>
			<el-button type="primary" @click="onConfirm">确定</el-button>
		</span>
	</el-dialog>
</template>

<script>
export default {
	name: 'GroupManageDialog',
	props: {
		visible: { type: Boolean, default: false },
		sceneId: { type: String, default: '' },
		groups: { type: Array, default: () => [] },
	},
	data() {
		return {
			customGroups: [],
			newGroupName: '',
			showAddInput: false,
			editingGroupId: null,
			editingGroupName: '',
			hoveredGroupId: null,
			isAddingNew: false
		};
	},
	computed: {
		visibleProxy: {
			get() { return this.visible; },
			set(v) { this.$emit('update:visible', v); }
		}
	},
	watch: {
		visible(val) {
			if (val) {
				this.updateCustomGroups();
			}
		},
		groups: {
			handler() {
				this.updateCustomGroups();
			},
			immediate: true
		}
	},
	methods: {
		updateCustomGroups() {
			// 从props.groups中筛选自定义分组（非系统默认分组）
			this.customGroups = (this.groups || []).filter(g => g && g.isCustom !== false).map(g => ({
				...g,
				replyCount: Array.isArray(g.replyList) ? g.replyList.length : 0
			}));
		},
		onDeleteGroup(group) {
            const self = this;
			this.$confirm(`确定要删除分组"${group.name}"吗？删除后该分组下的快捷短语也将被删除。`, '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				this.$http.post(API_ROOT.newcustomer + '/train/reply/deleteReplyType?id=' + group.id).then(res => {
					const result = res.data;
					if (result && result.success) {
						this.$message({ type: 'success', message: '删除成功' });
						this.$emit('refresh');
					} else {
						this.$message({ type: 'error', message: result.msg || '删除失败' });
					}
				}, () => {
					this.$message({ type: 'error', message: '删除失败' });
				});
			}).catch(() => {});
		},
		onStartAddGroup() {
			this.isAddingNew = true;
			this.newGroupName = '';
			this.$nextTick(() => {
				// 聚焦到输入框
				const inputs = this.$refs.newGroupInput;
				if (inputs && inputs.length > 0) {
					inputs[0].focus();
				}
			});
		},
		onSaveNewGroup() {
			if (!this.newGroupName || !this.newGroupName.trim()) {
				this.$message.warning('分组名称不能为空');
				return;
			}
			const self = this;
			this.$http.post(API_ROOT.newcustomer + '/train/reply/saveReplyType?sceneId=' + self.sceneId, {
				name: this.newGroupName.trim()
			}).then(res => {
				const result = res.data;
				if (result && result.success) {
					this.$message.success('新建成功');
					this.onCancelAdd();
					this.$emit('refresh');
				} else {
					this.$message.error(result.msg || '新建失败');
				}
			}).catch(err => {
				this.$message.error('新建失败');
			});
		},
		onCancelAdd() {
			this.isAddingNew = false;
			this.newGroupName = '';
		},
		onEditGroup(group) {
			// 确保只能有一个编辑状态
			this.editingGroupId = group.id;
			this.editingGroupName = group.name;
			this.$nextTick(() => {
				// 聚焦到输入框
				const inputs = this.$refs.editInput;
				if (inputs && inputs.length > 0) {
					inputs[0].focus();
				}
			});
		},
		onSaveGroup(group) {
			if (!this.editingGroupName || !this.editingGroupName.trim()) {
				this.$message.warning('分组名称不能为空');
				return;
			}
			if (this.editingGroupName.trim() === group.name) {
				// 名称未变化，直接取消编辑
				this.onCancelEdit();
				return;
			}
			this.$http.post(API_ROOT.newcustomer + '/train/reply/saveReplyType?sceneId=' + self.sceneId, {
				id: group.id,
				name: this.editingGroupName.trim(),
			}).then(res => {
				const result = res.data;
				if (result && result.success) {
					this.$message.success('保存成功');
					this.onCancelEdit();
					this.$emit('refresh');
				} else {
					this.$message.error(result.msg || '保存失败');
				}
			}).catch(err => {
				this.$message.error('保存失败');
			});
		},
		onCancelEdit() {
			this.editingGroupId = null;
			this.editingGroupName = '';
		},
		onConfirm() {
			this.visibleProxy = false;
		},
		onClosed() {
			this.customGroups = [];
			this.newGroupName = '';
			this.showAddInput = false;
			this.editingGroupId = null;
			this.editingGroupName = '';
			this.hoveredGroupId = null;
			this.isAddingNew = false;
		}
	}
};
</script>

<style lang='less' scoped>
.gm-dialog {
	/deep/ .el-dialog__header {
		background-color: #2d6cf6;
		color: #fff;
		padding: 12px 20px;
	}
	/deep/ .el-dialog__title { color: #fff; font-weight: 600; }
	/deep/ .el-dialog__headerbtn .el-dialog__close { color: #fff; }
	/deep/ .el-dialog__body { padding: 18px 24px; }
}

.gm-subtitle {
	color: #909399;
	font-size: 13px;
	margin-bottom: 16px;
}

.gm-table {
	border: 1px solid #ebeef5;
	border-radius: 4px;
	overflow: hidden;
	margin-bottom: 16px;
}

.gm-thead {
	display: grid;
	grid-template-columns: 1fr 120px 120px;
	background: #f5f7fa;
	border-bottom: 1px solid #ebeef5;
}


.gm-tr {
	display: grid;
	grid-template-columns: 1fr 120px 120px;
	border-bottom: 1px solid #f2f3f5;
	&:last-child { border-bottom: none; }
	&:hover { background: #f7faff; }
}

.gm-th, .gm-td {
	padding: 12px 16px;
	font-size: 14px;
	display: flex;
	align-items: center;
}

.gm-th {
	color: #606266;
	font-weight: 600;
	background: #f5f7fa;
}

.gm-td {
	color: #303133;
}

.gm-name { justify-content: flex-start; }
.gm-count { justify-content: center; }
.gm-actions { justify-content: center; }

.gm-name-text {
	cursor: pointer;
	padding: 4px 8px;
	border-radius: 4px;
	transition: all 0.2s;
	&:hover {
		background: #f0f9ff;
	}
}

.gm-name-hover {
	border: 1px solid #2d6cf6;
	background: #f0f9ff;
}

.gm-delete-btn {
	color: #f56c6c;
	padding: 0;
	&:hover { color: #f78989; }
}

.gm-cancel-btn {
	color: #909399;
	padding: 0;
	&:hover { color: #b1b3b8; }
}

.gm-add-row {
	background: #f8f9fa;
	border: 1px dashed #d9d9d9;
}

.gm-empty {
	text-align: center;
	color: #909399;
	padding: 40px 0;
	font-size: 14px;
}

.gm-add-group {
	text-align: center;
	margin-bottom: 16px;
}

.gm-add-btn {
	border: 1px solid #dcdfe6;
	color: #606266;
	background: #fff;
	padding: 8px 20px;
	&:hover {
		border-color: #2d6cf6;
		color: #2d6cf6;
	}
}
</style>



// WEBPACK FOOTER //
// ./src/page/train/FBT/components/GroupManageDialog.vue